import React, { useEffect, useState } from 'react';
import { Plus, Camera, Video, HardDrive, Scissors, BookImage, Check, StickyNote } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { workflowAPI } from '../utils/api';
import { toast } from 'sonner';

const EVENT_TYPES = ['Haldi', 'Mehndi', 'Sangeet', 'Wedding', 'Wedding & Reception', 'Reception'];

// 5 simple delivery milestones (tap to toggle done)
const MILESTONES = [
  { key: 'data_collected', label: 'Data', icon: HardDrive },
  { key: 'sent_to_editor', label: 'Editing', icon: Scissors },
  { key: 'photo_gallery_ready', label: 'Photos', icon: Camera },
  { key: 'video_ready', label: 'Video', icon: Video },
  { key: 'album_delivered', label: 'Album', icon: BookImage },
];

const isDone = (event, key) => {
  if (key === 'sent_to_editor') return event.photo_sent_to_editor === 'Y' || event.video_sent_to_editor === 'Y';
  return event[key] === 'Y';
};

const EventWorkflow = ({ embedded }) => {
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [filter, setFilter] = useState('all'); // all | active | delivered
  const [noteOpen, setNoteOpen] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const filterProject = searchParams.get('project') || 'all';
  const [formData, setFormData] = useState({ project_id: searchParams.get('project') || '', event_type: 'Haldi', event_date: '' });

  const getProjectsByYear = () => {
    const grouped = {};
    projects.forEach(p => {
      const year = p.wedding_start_date ? new Date(p.wedding_start_date).getFullYear().toString() : 'No Date';
      (grouped[year] = grouped[year] || []).push(p);
    });
    return Object.entries(grouped).sort((a, b) => (a[0] === 'No Date' ? 1 : b[0] === 'No Date' ? -1 : b[0] - a[0]));
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const fetchData = async () => {
    try {
      const projectParam = searchParams.get('project');
      const [eventsRes, projectsRes] = await Promise.all([
        workflowAPI.getEvents(projectParam || undefined),
        workflowAPI.getProjects(),
      ]);
      setEvents(eventsRes.data);
      setProjects(projectsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await workflowAPI.createEvent(formData);
      setShowForm(false);
      setFormData({ project_id: '', event_type: 'Haldi', event_date: '' });
      toast.success('Event added');
      fetchData();
    } catch {
      toast.error('Error creating event');
    }
  };

  const toggle = async (event, key) => {
    const val = isDone(event, key) ? 'N' : 'Y';
    const updates = key === 'sent_to_editor'
      ? { photo_sent_to_editor: val, video_sent_to_editor: val }
      : { [key]: val };
    // optimistic UI
    setEvents(prev => prev.map(ev => ev.id === event.id ? { ...ev, ...updates } : ev));
    setSavingId(event.id);
    try {
      await workflowAPI.updateEvent(event.id, updates);
    } catch {
      toast.error('Could not save — try again');
      fetchData();
    } finally {
      setSavingId(null);
    }
  };

  const saveNote = async (event, value) => {
    setEvents(prev => prev.map(ev => ev.id === event.id ? { ...ev, notes: value } : ev));
    try { await workflowAPI.updateEvent(event.id, { notes: value }); } catch { toast.error('Could not save note'); }
  };

  const getProject = (id) => projects.find(p => p.id === id);
  const projName = (id) => { const p = getProject(id); return p ? `${p.bride_name} & ${p.groom_name}` : 'Unknown'; };
  const projCode = (id) => { const p = getProject(id); return p ? p.project_code : ''; };

  const doneCount = (ev) => MILESTONES.filter(m => isDone(ev, m.key)).length;

  const filteredEvents = events.filter(ev => {
    const c = doneCount(ev);
    if (filter === 'active') return c < 5;
    if (filter === 'delivered') return c === 5;
    return true;
  });

  if (loading) {
    const body = <div className="flex items-center justify-center h-96"><div className="animate-pulse text-muted-foreground font-serif">Loading…</div></div>;
    return embedded ? body : <Layout>{body}</Layout>;
  }

  const body = (
    <>
      <div className="p-6 lg:p-10 max-w-4xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-serif tracking-tight" data-testid="workflow-events-title">
              {filterProject !== 'all' ? `${projName(filterProject)} — Events` : 'Delivery Tracker'}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">
              {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
              {filterProject !== 'all' && <a href="/workflow?tab=events" className="ml-2 text-gold hover:underline text-xs">(show all)</a>}
            </p>
          </div>
          <button onClick={() => setShowForm(!showForm)} className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground hover:bg-primary/90 transition-all text-sm font-medium rounded-lg" data-testid="add-event-button">
            <Plus className="h-4 w-4" /> {showForm ? 'Cancel' : 'Add Event'}
          </button>
        </div>

        {/* simple 3-way filter */}
        <div className="flex items-center gap-2 mb-6">
          {[['all', 'All'], ['active', 'In Progress'], ['delivered', 'Delivered']].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-4 py-1.5 text-sm rounded-full transition-all ${filter === k ? 'bg-primary text-primary-foreground' : 'bg-card border border-border hover:bg-accent'}`} data-testid={`filter-${k}`}>
              {label}
            </button>
          ))}
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="card p-5 mb-6 space-y-3" data-testid="add-event-form">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <select required value={formData.project_id} onChange={(e) => setFormData({ ...formData, project_id: e.target.value })} className="px-3 py-2.5 border border-input bg-background text-sm rounded-lg" data-testid="select-project">
                <option value="">Select couple</option>
                {getProjectsByYear().map(([year, yp]) => (
                  <optgroup key={year} label={`── ${year} ──`}>
                    {yp.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.bride_name} & {p.groom_name}</option>)}
                  </optgroup>
                ))}
              </select>
              <select required value={formData.event_type} onChange={(e) => setFormData({ ...formData, event_type: e.target.value })} className="px-3 py-2.5 border border-input bg-background text-sm rounded-lg" data-testid="select-event-type">
                {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <input type="date" required value={formData.event_date} onChange={(e) => setFormData({ ...formData, event_date: e.target.value })} className="px-3 py-2.5 border border-input bg-background text-sm rounded-lg" data-testid="event-date-input" />
            </div>
            <button type="submit" className="w-full py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium" data-testid="submit-event">Create Event</button>
          </form>
        )}

        <div className="space-y-3">
          {filteredEvents.length === 0 ? (
            <div className="card p-12 text-center">
              <p className="text-muted-foreground mb-3">No events found</p>
              <button onClick={() => setShowForm(true)} className="text-gold font-medium hover:underline">Add your first event</button>
            </div>
          ) : filteredEvents.map((event) => {
            const done = doneCount(event);
            const complete = done === 5;
            const accent = complete ? '#10b981' : done === 0 ? '#9ca3af' : '#f59e0b';
            return (
              <div key={event.id} className="card overflow-hidden" style={{ borderLeftWidth: 4, borderLeftColor: accent }} data-testid="event-card">
                <div className="p-4 sm:p-5">
                  {/* header */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {projCode(event.project_id) && <span className="text-[11px] font-bold text-gold bg-gold/10 px-2 py-0.5 rounded">{projCode(event.project_id)}</span>}
                        <span className="text-lg font-serif">{event.event_type}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">{projName(event.project_id)}{event.event_date ? ` · ${event.event_date}` : ''}</p>
                    </div>
                    <span className={`text-xs font-medium whitespace-nowrap ${complete ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`} data-testid="event-progress">
                      {complete ? 'Delivered ✓' : `${done}/5`}
                    </span>
                  </div>

                  {/* 5 tap-toggles */}
                  <div className="flex flex-wrap gap-2">
                    {MILESTONES.map((m) => {
                      const on = isDone(event, m.key);
                      const Icon = m.icon;
                      return (
                        <button
                          key={m.key}
                          onClick={() => toggle(event, m.key)}
                          disabled={savingId === event.id}
                          className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium border transition-all active:scale-95 disabled:opacity-60 ${on ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-transparent border-border text-muted-foreground hover:border-emerald-400 hover:text-foreground'}`}
                          data-testid={`toggle-${m.key}`}
                        >
                          {on ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                          {m.label}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setNoteOpen(noteOpen === event.id ? null : event.id)}
                      className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium border border-border text-muted-foreground hover:text-foreground transition-all"
                      data-testid="toggle-note"
                    >
                      <StickyNote className="h-3.5 w-3.5" /> Note
                    </button>
                  </div>

                  {noteOpen === event.id && (
                    <textarea
                      defaultValue={event.notes || ''}
                      onBlur={(e) => saveNote(event, e.target.value)}
                      placeholder="Add a quick note (saved when you tap away)…"
                      rows="2"
                      className="mt-3 w-full px-3 py-2 border border-input bg-background text-sm rounded-lg"
                      data-testid="event-note-input"
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
  return embedded ? body : <Layout>{body}</Layout>;
};

export default EventWorkflow;
